// engine
import { ArContainer, ArSprite, ArText, gsap, ORIENTATION } from '@aceroll/core';

const CARDS_CONFIG = [
	{
		name: 'Fireworks respin',
		title: 'fireworks_splash',
		titleStyle: 'splashFeatureTitle',
		description: 'firework_splash_desc',
		descriptionStyle: 'splashFeatureDescription',
		descriptionOffsetY: 20,
		//background: 'splash-fireworks_respin', // updated alias
		icon: 'splash-fireworks_respin',
		iconScale: 1,
		iconPosition: { x: 34, y: 128 }
	},
	{
		name: 'Burning sky bonus',
		title: 'burning_sky_splash',
		titleStyle: 'splashFeatureTitle',
		description: 'burning_sky_splash_desc',
		descriptionStyle: 'splashFeatureDescription',
		descriptionOffsetY: 20,
		//background: 'splash-burning_sky_bonus', // updated alias
		icon: 'splash-burning_sky_bonus',
		iconScale: 1,
		iconPosition: { x: 3, y: 150 }
	},
	{
		name: 'Buy bonusn',
		title: 'buy_bonus_splash',
		titleStyle: 'splashFeatureTitle',
		description: 'buy_bonus_splash_desc',
		descriptionStyle: 'splashFeatureDescription',
		descriptionOffsetY: 20,
		//background: 'splash-buy_bonus', // updated alias
		icon: 'splash-buy_bonus',
		iconScale: 1,
		iconPosition: { x: -16, y: 102 }
	}
];

const CARDS_POSITIONS = {
	LANDSCAPE: [-620, 0, 620],
	PORTRAIT: [-1400, 230, 1630]
};

const CONTENT_SCALE = {
	LANDSCAPE: 1,
	PORTRAIT: 1
};

export class SplashFeatures extends ArContainer {
	constructor(orientation) {
		super('splash-popup');

		this._cards = [];
		this._content = new ArContainer('Content Container');
		this._orientation = orientation;
		this._currentScale = null;

		this.createCardsContainers();
		this.createCards();
		this.placeCards();
		this.addChild(this._content);
	}

	createCardsContainers() {
		this._landscapeCardsContainer = this._content.addChild(new ArContainer('Landscape Cards'));

		this._portraitCardsContainer = this._content.addChild(new ArContainer('Portrait Cards'));
	}

	createCards() {
		for (const cardConfig of CARDS_CONFIG) {
			const card = this.createCard(cardConfig);
			this._cards.push(card);
		}
	}

	createCard(config) {
		const card = new ArContainer(config.name);

		if (config.background) {
			const background = ArSprite.from(config.icon, config.name);
			background.anchor.set(0.5, 0.5);
			card.addChild(background);
		}

		if (config.icon) {
			const icon = card.addChild(ArSprite.from(config.icon));
			icon.anchor.set(0.5, 0.5);
			icon.position.set(config.iconPosition.x, config.iconPosition.y);
			icon.scale.set(config.iconScale);
		}
		const title = card.addChild(ArText.from(config.title, config.titleStyle, config.name));
		title.anchor.set(0.5, 0.5);
		title.position.set(0, 215);

		const description = card.addChild(ArText.from(config.description, config.descriptionStyle, config.name));
		description.anchor.set(0.5, 0);
		description.position.set(0, 250 + config.descriptionOffsetY);

		return card;
	}

	scaleContent() {
		this.clearTimeline(this._contentScaleTimeline);

		this._contentScaleTimeline = gsap.timeline();
		const { scaleFrom, scaleTo } = this.contentScale;
		this._content.scale.set(scaleFrom);

		this._contentScaleTimeline
			.to(this._content.scale, {
				x: scaleTo,
				y: scaleTo,
				duration: 0.5,
				onComplete: () => {
					this._currentScale = scaleTo;
				}
			});
	}

	placeCards() {
		if (this._orientation === ORIENTATION.LANDSCAPE) {
			this._portraitCardsContainer.removeChildren();
			this.updateCardsStartPosition(this._landscapeCardsContainer);
		} else if (this._orientation === ORIENTATION.PORTRAIT) {
			this._landscapeCardsContainer.removeChildren();
			this.updateCardsStartPosition(this._portraitCardsContainer);
		} else {
			throw new Error('Set proper orientation! Current orientation is: ', this._orientation);
		}
	}

	startPortraitCardSequence() {
		this.clearTimeline(this._portraitCardTimeline);

		this._portraitCardTimeline = gsap.timeline({
			repeat: -1,
			repeatDelay: 0
		});

		this.setupCardSequence();
	}

	setupCardSequence() {
		const timeline = this._portraitCardTimeline;
		const cards = [...this._cards];
		const centerX = CARDS_POSITIONS.PORTRAIT[1];
		const rightX = CARDS_POSITIONS.PORTRAIT[2];
		const leftX = CARDS_POSITIONS.PORTRAIT[0];

		cards.forEach((card) => card.x = leftX);

		let currentIndex = cards.length - 1;
		cards[currentIndex].x = centerX;

		for (let i = 0; i < cards.length; i++) {
			const currentCard = cards[currentIndex];
			const nextIndex = (currentIndex - 1 + cards.length) % cards.length;
			const nextCard = cards[nextIndex];

			timeline
				.to(currentCard, {
					delay: 1.5
				})
				.to(currentCard, {
					x: rightX,
					duration: 0.5,
					onComplete: () => {
						currentCard.x = leftX;
					}
				})
				.to(nextCard, {
					x: centerX,
					duration: 0.5
				});

			currentIndex = nextIndex;
		}
	}

	updateCardsStartPosition(container) {
		this._cards.forEach((card, i) => {
			const scale = this._orientation === ORIENTATION.LANDSCAPE ? 1 : 1;
			card.scale.set(scale);

			container.addChild(card);
			card.x = CARDS_POSITIONS[this._orientation.toUpperCase()][i];
		});
	}

	setLandscape() {
		this._orientation = ORIENTATION.LANDSCAPE;
		this.scaleContent();
		this.clearTimeline(this._portraitCardTimeline);
		this.placeCards();
	};

	setPortrait() {
		this._orientation = ORIENTATION.PORTRAIT;
		this.scaleContent();
		this.placeCards();
		this.startPortraitCardSequence();
	}

	clearTimeline(timeline) {
		if (timeline) {
			timeline.kill();
			timeline = null;
		}
	}

	hide() {
		this.clearTimeline(this._portraitCardTimeline);
		this.clearTimeline(this._contentScaleTimeline);
	}

	get contentScale() {
		const isLandscape = this._orientation === ORIENTATION.LANDSCAPE;
		const baseScaleFrom = isLandscape ? CONTENT_SCALE.PORTRAIT : CONTENT_SCALE.LANDSCAPE;
		const scaleFrom = this._currentScale ? this._currentScale : baseScaleFrom;
		const scaleTo = isLandscape ? CONTENT_SCALE.LANDSCAPE : CONTENT_SCALE.PORTRAIT;

		return { scaleFrom, scaleTo };
	}
}
